import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';
import {
  Download as DownloadIcon,
  Search as SearchIcon,
  FactCheck as FactCheckIcon,
  AccessTime as AccessTimeIcon,
  WarningAmber as WarningIcon,
  CheckCircleOutline as CheckIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs, { Dayjs } from 'dayjs';
import DashboardLayout from '../components/DashboardLayout';
import EmployeeFilter from '../components/EmployeeFilter';
import { useAppSelector, useAppDispatch } from '../store';
import { useEmployeeRecords } from '../hooks/useEmployeeRecords';
import { apiService } from '../services/api';
import { showNotification } from '../store/notificationSlice';
import { fetchGlobalSettings } from '../store/applicationSettingsSlice';
import type { ShiftDistribution } from '../types/shiftDistributions';
import type { Shift } from '../types/shifts';

interface AutopsyRow {
  employeeInternalId: string;
  biometricId: string;
  employeeName: string;
  shiftName: string;
  workStartTime: string;
  inTime: string;
  outTime: string;
  isLate: number;
  lateMin: number;
}

const MIN_PANEL_WIDTH = 450;

function parseHHmmssToSeconds(timeStr: string): number {
  if (!timeStr || timeStr.length < 8) return 0;
  const parts = timeStr.substring(0, 8).split(':');
  if (parts.length < 3) return 0;
  const h = parseInt(parts[0], 10) || 0;
  const m = parseInt(parts[1], 10) || 0;
  const s = parseInt(parts[2], 10) || 0;
  return h * 3600 + m * 60 + s;
}

function resolveFieldValue(fieldVal: any, targetDate: string): any {
  if (fieldVal === null || fieldVal === undefined) return null;
  if (!Array.isArray(fieldVal)) return fieldVal;
  let bestVal: any = null;
  let bestDate = '';
  for (const item of fieldVal) {
    if (item && typeof item === 'object') {
      const { date, value } = item;
      if (date && value !== undefined && date <= targetDate) {
        if (!bestDate || date > bestDate) {
          bestDate = date;
          bestVal = value;
        }
      }
    }
  }
  return bestVal;
}

function employeeHasTag(empData: Record<string, any>, tagId: string, targetDate: string): boolean {
  for (const key of Object.keys(empData)) {
    const resolved = resolveFieldValue(empData[key], targetDate);
    if (typeof resolved === 'string') {
      const parts = resolved.split(',').map((p) => p.trim());
      if (parts.includes(tagId)) {
        return true;
      }
    }
  }
  return false;
}

function getEffectiveShiftDistribution(
  distributions: ShiftDistribution[],
  targetDate: string
): ShiftDistribution | null {
  if (!distributions || distributions.length === 0) return null;
  const applicable = distributions
    .filter((d) => d.date <= targetDate)
    .sort((a, b) => b.date.localeCompare(a.date));
  return applicable.length > 0 ? applicable[0] : distributions[0];
}

function resolveShiftForEmployee(
  dist: ShiftDistribution | null,
  empData: Record<string, any> | undefined,
  internalId: string,
  targetDate: string,
  fallbackShiftId?: string
): { shiftId: string; shiftType: string } {
  if (dist) {
    if (Array.isArray(dist.shiftApplicationSerial)) {
      for (const item of dist.shiftApplicationSerial) {
        if ((item.elementType === 'employee' || (item.elementType as string) === 'Employee') && item.elementId === internalId) {
          return { shiftId: item.shiftId, shiftType: item.shiftType };
        }
        if ((item.elementType === 'tag' || (item.elementType as string) === 'Tag') && empData && employeeHasTag(empData, item.elementId, targetDate)) {
          return { shiftId: item.shiftId, shiftType: item.shiftType };
        }
      }
    }
    if (dist.defaultShiftId) {
      return { shiftId: dist.defaultShiftId, shiftType: dist.defaultShiftType || 'shift' };
    }
  }

  return { shiftId: fallbackShiftId || '', shiftType: 'shift' };
}

export default function DataAutopsy() {
  const dispatch = useAppDispatch();
  const selectedUnit = useAppSelector((s) => s.auth.selectedUnit);
  const employeeList = useAppSelector((s) => s.employees.list);
  const uiFields = useAppSelector((s) => s.applicationSettings.uiFields);
  const dbKey = selectedUnit
    ? `cranberry-employees-${selectedUnit.tenant}-${selectedUnit.solution}-${selectedUnit.unit}`
    : '';

  const globalSettings = useAppSelector((s) => s.applicationSettings.globalSettings);
  const employeeRecords = useEmployeeRecords(dbKey);

  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(dayjs());
  const [filterShiftValue, setFilterShiftValue] = useState<string>(''); // "shift:id" or "group:id"
  const [includeAbsent, setIncludeAbsent] = useState<boolean>(false);
  const [filteredUUIDs, setFilteredUUIDs] = useState<string[] | null>(null);

  const [shifts, setShifts] = useState<{
    id: string;
    name: string;
    shiftStartTime: string;
    workStartTime: string;
    workEndTime: string;
    shiftEndTime: string;
    lateConsideration: number;
  }[]>([]);
  const [shiftGroups, setShiftGroups] = useState<{ id: string; name: string; shiftIds: string[] }[]>([]);

  const [rows, setRows] = useState<AutopsyRow[]>([]);
  const [processed, setProcessed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [progressText, setProgressText] = useState<string>('');

  // Pagination for preview table
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);

  // Resizable left panel
  const [panelWidth, setPanelWidth] = useState(MIN_PANEL_WIDTH);
  const [isDragging, setIsDragging] = useState(false);
  const dragStartX = useRef(0);
  const dragStartWidth = useRef(0);

  const handleResizeStart = (e: React.MouseEvent) => {
    dragStartX.current = e.clientX;
    dragStartWidth.current = panelWidth;
    setIsDragging(true);
    e.preventDefault();
  };

  useEffect(() => {
    if (!isDragging) return;
    const onMove = (e: MouseEvent) => {
      const newWidth = Math.max(MIN_PANEL_WIDTH, dragStartWidth.current + (e.clientX - dragStartX.current));
      setPanelWidth(newWidth);
    };
    const onUp = () => setIsDragging(false);
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
  }, [isDragging]);

  // Load Shifts, Groups, and Global Settings on mount
  useEffect(() => {
    dispatch(fetchGlobalSettings());
    const loadShiftsAndGroups = async () => {
      try {
        const shiftsRes = await apiService.listShifts();
        const loadedShifts = (shiftsRes.data ?? []).map((s: Shift) => ({
          id: s.shiftId,
          name: s.shiftName,
          shiftStartTime: s.shiftStartTime || s.workStartTime || '00:00:00',
          workStartTime: s.workStartTime || s.shiftStartTime || '08:00:00',
          workEndTime: s.workEndTime || '17:00:00',
          shiftEndTime: s.shiftEndTime || s.workEndTime || '23:59:59',
          lateConsideration: s.lateConsideration ?? 0,
        }));
        setShifts(loadedShifts);

        const groupsRes = await apiService.listShiftGroups();
        const loadedGroups = (groupsRes.data ?? []).map((g: any) => ({
          id: g.shiftGroupId,
          name: g.shiftGroupName,
          shiftIds: g.shiftIds || [],
        }));
        setShiftGroups(loadedGroups);
      } catch (err) {
        console.error('Failed to load shifts/groups', err);
      }
    };
    loadShiftsAndGroups();
  }, [dispatch]);

  // Compute target candidates based on filter
  const targetCandidates = useMemo(() => {
    const candidates = filteredUUIDs
      ? employeeList.filter((e) => filteredUUIDs.includes(e.employeeUUID))
      : employeeList;
    return candidates.filter((e) => e.biometricId || e.employeeInternalId);
  }, [filteredUUIDs, employeeList]);

  // Core processing function
  const processAutopsyData = useCallback(
    async (targetDate: Dayjs, candidates: typeof employeeList): Promise<AutopsyRow[]> => {
      const dateStr = targetDate.format('YYYY-MM-DD');

      // 1. Fetch shift distributions and resolve effective distribution for date
      let dist: ShiftDistribution | null = null;
      try {
        const distsRes = await apiService.listShiftDistributions();
        dist = getEffectiveShiftDistribution(distsRes.data ?? [], dateStr);
      } catch (e) {
        console.warn('Could not load shift distributions', e);
      }

      // 2. Query punches for all candidates in parallel chunks of 100
      const CHUNK_SIZE = 100;
      const chunks: { employeeInternalId: string; biometricId: string }[][] = [];
      for (let i = 0; i < candidates.length; i += CHUNK_SIZE) {
        chunks.push(
          candidates.slice(i, i + CHUNK_SIZE).map((c) => ({
            employeeInternalId: c.employeeInternalId,
            biometricId: c.biometricId || c.employeeInternalId,
          }))
        );
      }

      const punchResults: Record<string, any[]> = {};
      const CONCURRENCY = 4;
      for (let i = 0; i < chunks.length; i += CONCURRENCY) {
        const batchOfChunks = chunks.slice(i, i + CONCURRENCY);
        const processedSoFar = Math.min((i + CONCURRENCY) * CHUNK_SIZE, candidates.length);
        setProgressText(`Fetching punches: ${processedSoFar} / ${candidates.length} employees…`);

        await Promise.all(
          batchOfChunks.map(async (batch) => {
            try {
              const res = await apiService.queryManualTimekeepingEntries({
                date: dateStr,
                employees: batch,
              });
              const data = res.data?.data ?? {};
              Object.assign(punchResults, data);
            } catch (err) {
              console.error('Chunk query failed', err);
            }
          })
        );
      }
      setProgressText('Calculating attendance & shift hours…');

      // 3. Build lookup maps for shifts
      const shiftMap = new Map<
        string,
        {
          id: string;
          name: string;
          shiftStartTime: string;
          workStartTime: string;
          workEndTime: string;
          shiftEndTime: string;
          lateConsideration: number;
        }
      >();
      shifts.forEach((s) => shiftMap.set(s.id, s));

      const groupMap = new Map<string, { id: string; name: string; shiftIds: string[] }>();
      shiftGroups.forEach((g) => groupMap.set(g.id, g));

      // Build lookup map for employee records
      const employeeRecordMap = new Map<string, Record<string, any>>();
      for (const rec of employeeRecords) {
        const uuid = String(rec['employeeUUID'] ?? '');
        if (uuid) {
          employeeRecordMap.set(uuid, rec as Record<string, any>);
        }
      }

      // 4. Evaluate each employee
      const autopsyRows: AutopsyRow[] = [];
      const isStrict = globalSettings?.timekeepingErrorDetectionModeStrict ?? false;

      for (const emp of candidates) {
        const empFullRecord = employeeRecordMap.get(emp.employeeUUID);

        // Resolve shift
        const fallbackShiftId = shifts[0]?.id || '';
        const { shiftId, shiftType } = resolveShiftForEmployee(
          dist,
          empFullRecord,
          emp.employeeInternalId,
          dateStr,
          fallbackShiftId
        );

        // Apply shift filter if user selected one
        if (filterShiftValue.startsWith('shift:')) {
          const expectedShiftId = filterShiftValue.substring(6);
          if (shiftId !== expectedShiftId) continue;
        } else if (filterShiftValue.startsWith('group:')) {
          const expectedGroupId = filterShiftValue.substring(6);
          if ((shiftType !== 'shift_group' && shiftType !== 'ShiftGroup') || shiftId !== expectedGroupId) continue;
        }

        const fallbackShift = shifts[0];
        let effectiveShiftName = fallbackShift?.name || 'Default Shift';
        let shiftStartTimeStr = fallbackShift?.shiftStartTime || '00:00:00';
        let workStartTimeStr = fallbackShift?.workStartTime || '08:00:00';
        let shiftEndTimeStr = fallbackShift?.shiftEndTime || '23:59:59';
        let lateConsiderationMin = fallbackShift?.lateConsideration ?? 0;

        if ((shiftType === 'shift' || shiftType === 'Shift') && shiftMap.has(shiftId)) {
          const s = shiftMap.get(shiftId)!;
          effectiveShiftName = s.name;
          shiftStartTimeStr = s.shiftStartTime || '00:00:00';
          workStartTimeStr = s.workStartTime;
          shiftEndTimeStr = s.shiftEndTime;
          lateConsiderationMin = s.lateConsideration ?? 0;
        } else if ((shiftType === 'shift_group' || shiftType === 'ShiftGroup') && groupMap.has(shiftId)) {
          const g = groupMap.get(shiftId)!;
          effectiveShiftName = g.name;
          if (g.shiftIds.length > 0 && shiftMap.has(g.shiftIds[0])) {
            const s = shiftMap.get(g.shiftIds[0])!;
            shiftStartTimeStr = s.shiftStartTime || '00:00:00';
            workStartTimeStr = s.workStartTime;
            shiftEndTimeStr = s.shiftEndTime;
            lateConsiderationMin = s.lateConsideration ?? 0;
          }
        }

        // Get punches
        const rawPunches = punchResults[emp.employeeInternalId] || [];
        const shiftStartSec = parseHHmmssToSeconds(shiftStartTimeStr);
        let shiftEndSec = parseHHmmssToSeconds(shiftEndTimeStr);
        const isCrossMidnight = shiftEndSec > 0 && shiftEndSec <= shiftStartSec;
        if (isCrossMidnight) {
          shiftEndSec += 86400;
        }

        // Filter punches: ignore deactivated punches and punches before shift start time of that day
        const activePunches = rawPunches.filter((p) => {
          if (p.isDeactivated) return false;
          if (shiftStartSec > 0 && parseHHmmssToSeconds(p.time) < shiftStartSec) {
            return false;
          }
          return true;
        });

        if (activePunches.length === 0) {
          if (includeAbsent) {
            autopsyRows.push({
              employeeInternalId: emp.employeeInternalId,
              biometricId: emp.biometricId,
              employeeName: emp.employeeName,
              shiftName: effectiveShiftName,
              workStartTime: workStartTimeStr,
              inTime: '',
              outTime: '',
              isLate: 0,
              lateMin: 0,
            });
          }
          continue;
        }

        // Sort active punches ascending
        activePunches.sort((a, b) => a.time.localeCompare(b.time));

        const rawInTime = activePunches[0].time;
        const inTime = rawInTime; // Raw punch time; adjustments handled downstream in processes

        // When Timekeeping Error Detection Strict is Loose (!isStrict):
        // take the last entry before shift end as out time
        let outTime = '';
        if (!isStrict) {
          const punchesBeforeShiftEnd = activePunches.filter((p) => {
            const pSec = parseHHmmssToSeconds(p.time);
            if (!isCrossMidnight && shiftEndSec > 0 && pSec > shiftEndSec) {
              return false;
            }
            return true;
          });
          if (punchesBeforeShiftEnd.length >= 2) {
            outTime = punchesBeforeShiftEnd[punchesBeforeShiftEnd.length - 1].time;
          }
        } else {
          outTime = activePunches.length >= 2 ? activePunches[activePunches.length - 1].time : '';
        }

        // Calculate late using lateConsideration
        const inSec = parseHHmmssToSeconds(inTime);
        const workStartSec = parseHHmmssToSeconds(workStartTimeStr);
        const diffSec = inSec - workStartSec;
        const lateThresholdSec = lateConsiderationMin * 60;

        let isLate = 0;
        let lateMin = 0;

        if (diffSec > lateThresholdSec) {
          isLate = 1;
          lateMin = Math.floor(diffSec / 60);
        }

        autopsyRows.push({
          employeeInternalId: emp.employeeInternalId,
          biometricId: emp.biometricId,
          employeeName: emp.employeeName,
          shiftName: effectiveShiftName,
          workStartTime: workStartTimeStr,
          inTime,
          outTime,
          isLate,
          lateMin,
        });
      }

      // Sort rows by employeeInternalId ascending
      autopsyRows.sort((a, b) => a.employeeInternalId.localeCompare(b.employeeInternalId));
      return autopsyRows;
    },
    [shifts, shiftGroups, employeeRecords, filterShiftValue, includeAbsent, globalSettings]
  );

  // Generate text file content exactly as requested
  const generateAutopsyText = (dataRows: AutopsyRow[]): string => {
    const lines = ['Id,in time, out time, isLate, Late min'];
    for (const r of dataRows) {
      lines.push(`${r.employeeInternalId},${r.inTime},${r.outTime},${r.isLate},${r.lateMin}`);
    }
    return lines.join('\n');
  };

  // Download trigger
  const triggerFileDownload = (content: string, filename: string) => {
    const blob = new Blob([content], { type: 'text/plain;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Handler for previewing data on screen
  const handleProcess = async () => {
    if (!selectedDate?.isValid()) return;
    setLoading(true);
    try {
      const result = await processAutopsyData(selectedDate, targetCandidates);
      setRows(result);
      setProcessed(true);
      setPage(0);
      dispatch(
        showNotification({
          message: `Processed Data Autopsy for ${result.length} employee records.`,
          severity: 'success',
        })
      );
    } catch (err: any) {
      dispatch(
        showNotification({
          message: err?.message || 'Failed to process Data Autopsy.',
          severity: 'error',
        })
      );
    } finally {
      setLoading(false);
    }
  };

  // Handler for one-click download
  const handleDownload = async () => {
    if (!selectedDate?.isValid()) return;
    setDownloading(true);
    try {
      let dataRows = rows;
      if (!processed || rows.length === 0) {
        dataRows = await processAutopsyData(selectedDate, targetCandidates);
        setRows(dataRows);
        setProcessed(true);
      }

      if (dataRows.length === 0) {
        dispatch(
          showNotification({
            message: 'No employee attendance records found for the selected criteria.',
            severity: 'warning',
          })
        );
        return;
      }

      const textContent = generateAutopsyText(dataRows);
      const filename = `data_autopsy_${selectedDate.format('YYYY-MM-DD')}.txt`;
      triggerFileDownload(textContent, filename);

      dispatch(
        showNotification({
          message: `Downloaded ${filename} successfully (${dataRows.length} rows).`,
          severity: 'success',
        })
      );
    } catch (err: any) {
      dispatch(
        showNotification({
          message: err?.message || 'Failed to generate download file.',
          severity: 'error',
        })
      );
    } finally {
      setDownloading(false);
    }
  };

  const stats = useMemo(() => {
    const total = rows.length;
    const lateCount = rows.filter((r) => r.isLate === 1).length;
    const onTimeCount = rows.filter((r) => r.isLate === 0 && r.inTime !== '').length;
    const missingOut = rows.filter((r) => r.inTime !== '' && r.outTime === '').length;
    return { total, lateCount, onTimeCount, missingOut };
  }, [rows]);

  return (
    <DashboardLayout>
      <Box sx={{ display: 'flex', m: -3, minHeight: 'calc(100vh - 64px)' }}>
        {/* Left Filter Panel (identical to Error Correction) */}
        <Box
          sx={{
            width: panelWidth,
            minWidth: MIN_PANEL_WIDTH,
            flexShrink: 0,
            borderRight: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
            p: 3,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <FactCheckIcon color="primary" />
            <Typography variant="h6" fontWeight={600}>
              Filter Employees
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" mb={2.5}>
            Select departments, tags, or fields to filter employees, or leave blank to process all employees.
          </Typography>

          {uiFields.length > 0 && (
            <EmployeeFilter
              uiFields={uiFields}
              employees={employeeRecords}
              onChange={(uuids) => {
                setFilteredUUIDs(uuids);
                setProcessed(false);
              }}
              onSearch={handleProcess}
              searchDisabled={!selectedDate?.isValid() || loading || downloading}
            />
          )}
        </Box>

        {/* Drag handle for resizing left panel */}
        <Box
          onMouseDown={handleResizeStart}
          sx={{
            width: '4px',
            cursor: 'col-resize',
            bgcolor: isDragging ? 'primary.main' : 'transparent',
            transition: 'background-color 0.2s',
            '&:hover': { bgcolor: 'primary.light' },
          }}
        />

        {/* Right Main Content Panel */}
        <Box sx={{ flexGrow: 1, p: 3, display: 'flex', flexDirection: 'column', overflowX: 'hidden' }}>
          {/* Top Control Bar */}
          <Paper variant="outlined" sx={{ p: 2.5, mb: 3 }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
              <DatePicker
                label="Date"
                value={selectedDate}
                onChange={(d) => {
                  setSelectedDate(d);
                  setProcessed(false);
                }}
                slotProps={{ textField: { size: 'small', sx: { width: 170 } } }}
              />

              <FormControl size="small" sx={{ minWidth: 220 }}>
                <InputLabel>Shift / Shift Group</InputLabel>
                <Select
                  value={filterShiftValue}
                  onChange={(e) => {
                    setFilterShiftValue(e.target.value);
                    setProcessed(false);
                  }}
                  label="Shift / Shift Group"
                >
                  <MenuItem value="">
                    <em>All Shifts</em>
                  </MenuItem>
                  {shifts.map((s) => (
                    <MenuItem key={`shift:${s.id}`} value={`shift:${s.id}`}>
                      Shift: {s.name}
                    </MenuItem>
                  ))}
                  {shiftGroups.map((g) => (
                    <MenuItem key={`group:${g.id}`} value={`group:${g.id}`}>
                      Group: {g.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Chip
                label={filteredUUIDs ? `Filtered (${targetCandidates.length})` : `All Employees (${targetCandidates.length})`}
                color={filteredUUIDs ? 'primary' : 'default'}
                variant="outlined"
                size="small"
                sx={{ fontWeight: 500 }}
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={includeAbsent}
                    onChange={(e) => {
                      setIncludeAbsent(e.target.checked);
                      setProcessed(false);
                    }}
                    size="small"
                  />
                }
                label={<Typography variant="body2">Include absent (no punches)</Typography>}
              />

              <Box sx={{ ml: 'auto', display: 'flex', gap: 1.5 }}>
                <Button
                  variant="outlined"
                  startIcon={loading ? <CircularProgress size={16} /> : <SearchIcon />}
                  onClick={handleProcess}
                  disabled={loading || downloading || !selectedDate?.isValid()}
                  sx={{ px: 2.5 }}
                >
                  {loading ? 'Processing…' : 'Preview'}
                </Button>

                <Button
                  variant="contained"
                  color="primary"
                  startIcon={downloading ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />}
                  onClick={handleDownload}
                  disabled={loading || downloading || !selectedDate?.isValid()}
                  sx={{ px: 3, fontWeight: 600 }}
                >
                  {downloading ? 'Downloading…' : 'Download Data Autopsy'}
                </Button>
              </Box>
            </Box>
          </Paper>

          {/* Quick Stats Banner */}
          {processed && rows.length > 0 && (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 2, mb: 3 }}>
              <Card variant="outlined">
                <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                  <Typography variant="caption" color="text.secondary" fontWeight={500}>
                    Total Attendance Rows
                  </Typography>
                  <Typography variant="h5" fontWeight={700}>
                    {stats.total}
                  </Typography>
                </CardContent>
              </Card>

              <Card variant="outlined" sx={{ borderLeft: '4px solid #f44336' }}>
                <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <WarningIcon sx={{ fontSize: 18, color: '#f44336' }} />
                    <Typography variant="caption" color="text.secondary" fontWeight={500}>
                      Late Employees
                    </Typography>
                  </Box>
                  <Typography variant="h5" fontWeight={700} color="error.main">
                    {stats.lateCount}
                  </Typography>
                </CardContent>
              </Card>

              <Card variant="outlined" sx={{ borderLeft: '4px solid #4caf50' }}>
                <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <CheckIcon sx={{ fontSize: 18, color: '#4caf50' }} />
                    <Typography variant="caption" color="text.secondary" fontWeight={500}>
                      On Time Employees
                    </Typography>
                  </Box>
                  <Typography variant="h5" fontWeight={700} color="success.main">
                    {stats.onTimeCount}
                  </Typography>
                </CardContent>
              </Card>

              <Card variant="outlined" sx={{ borderLeft: '4px solid #ff9800' }}>
                <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <AccessTimeIcon sx={{ fontSize: 18, color: '#ff9800' }} />
                    <Typography variant="caption" color="text.secondary" fontWeight={500}>
                      Missing Out Punch
                    </Typography>
                  </Box>
                  <Typography variant="h5" fontWeight={700} color="warning.main">
                    {stats.missingOut}
                  </Typography>
                </CardContent>
              </Card>
            </Box>
          )}

          {/* Data Table Preview */}
          <TableContainer
            component={Paper}
            variant="outlined"
            sx={{ flexGrow: 1, maxHeight: 'calc(100vh - 380px)', minHeight: 300 }}
          >
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Id</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Employee Name</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Shift</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>In Time</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Out Time</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>isLate</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Late min</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                      <CircularProgress size={36} />
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                        {progressText || 'Processing attendance and punch records…'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                      <Typography variant="body1" color="text.secondary">
                        {processed
                          ? 'No attendance records found matching your filters.'
                          : 'Select a date and click "Preview" or "Download Data Autopsy".'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.slice(page * pageSize, page * pageSize + pageSize).map((row) => (
                    <TableRow key={row.employeeInternalId} hover>
                      <TableCell sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                        {row.employeeInternalId}
                      </TableCell>
                      <TableCell>{row.employeeName}</TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {row.shiftName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Start: {row.workStartTime}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'monospace' }}>
                        {row.inTime || <Typography variant="caption" color="text.disabled">—</Typography>}
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'monospace' }}>
                        {row.outTime || <Typography variant="caption" color="text.disabled">—</Typography>}
                      </TableCell>
                      <TableCell>
                        {row.isLate === 1 ? (
                          <Chip label="1 (Late)" size="small" color="error" variant="outlined" sx={{ fontWeight: 600 }} />
                        ) : (
                          <Chip label="0 (On Time)" size="small" color="success" variant="outlined" sx={{ fontWeight: 500 }} />
                        )}
                      </TableCell>
                      <TableCell sx={{ fontWeight: row.lateMin > 0 ? 700 : 400, color: row.lateMin > 0 ? 'error.main' : 'inherit' }}>
                        {row.lateMin}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          {rows.length > 0 && (
            <TablePagination
              component="div"
              count={rows.length}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={pageSize}
              onRowsPerPageChange={(e) => {
                setPageSize(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[25, 50, 100, 200]}
              sx={{ borderTop: '1px solid', borderColor: 'divider' }}
            />
          )}
        </Box>
      </Box>
    </DashboardLayout>
  );
}
